import { useRef } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { fg } from '@atlaskit/platform-feature-flags';
import { getPerformanceMark } from '../../../../performance-profiling';
import { usePerformanceAnalytics } from '../../../../performance-profiling/usePerformanceAnalytics';
import {
	isTokenValid,
	authTokenForExtensionMutation,
	MILLIS_IN_SECOND,
} from '../useProductFetchClient';
import type { AuthToken, MutationData, MutationVariables } from '../useProductFetchClient';
import { createAppAri } from '@atlassian/cs-ari';
import type { ContextId, ExtensionId } from '@atlassian/forge-ui-types';
import { useTracingContext } from '../../../../error-reporting';
import { createAtlAttributionHeader } from '../../../../utils';
import type {
	EgressSetPayload,
	EgressGetPayload,
	EgressDeleteDomainPayload,
	EgressDeleteGroupPayload,
	RemoteGetPayload,
	RemoteSetPayload,
} from './types';
import type { usePermissionProps } from './usePermission';

export type PermissionFetchClient = {
	egressDeleteDomain: (payload: EgressDeleteDomainPayload) => Promise<Response>;
	egressDeleteGroup: (payload: EgressDeleteGroupPayload) => Promise<Response>;
	egressGet: (payload: EgressGetPayload) => Promise<Response>;
	egressSet: (payload: EgressSetPayload, isDryRun: boolean) => Promise<Response>;
	remoteGet: (payload: RemoteGetPayload) => Promise<Response>;
	remoteSet: (payload: RemoteSetPayload, isDryRun: boolean) => Promise<Response>;
};

const EGRESS_GET = () => ({
	method: 'GET',
	route: '/forge/installation/v1/dynamic/egress',
});

const EGRESS_SET = (installationId: string, dryRun: boolean = false) => ({
	method: 'PUT',
	route: `/forge/installation/v1/${installationId}/dynamic/egress?dryRun=${dryRun}`,
});

const EGRESS_DELETE_GROUP = (installationId: string) => ({
	method: 'DELETE',
	route: `/forge/installation/v1/${installationId}/dynamic/egress`,
});

const EGRESS_DELETE_DOMAIN = (installationId: string) => ({
	method: 'PATCH',
	route: `/forge/installation/v1/${installationId}/dynamic/egress/delete-domain`,
});

const REMOTE_GET = () => ({
	method: 'GET',
	route: '/forge/installation/v1/dynamic/remote',
});

const REMOTE_SET = (installationId: string, dryRun: boolean = false) => ({
	method: 'PUT',
	route: `/forge/installation/v1/${installationId}/dynamic/remote?dryRun=${dryRun}`,
});

export function usePermissionFetchClient({
	appId,
	installationId,
	extensionId,
	accountType,
	renderThreeLOPrompt,
	options,
	contextIds = [],
}: usePermissionProps): PermissionFetchClient {
	function getBaseUrl(): string {
		const currentUrl = new URL(document.URL);

		return `${currentUrl.origin}/gateway/api`;
	}

	const authTokenRef = useRef<AuthToken | undefined>(undefined);

	const { captureEvent } = usePerformanceAnalytics();
	const [userAuthTokenForExtensionMutationFunction] = useMutation<MutationData, MutationVariables>(
		authTokenForExtensionMutation,
		options,
	);

	const tracing = useTracingContext();

	const fetchOAuthToken = async (
		extensionId: ExtensionId,
		contextIds: ContextId[],
	): Promise<AuthToken> => {
		tracing?.recordGqlCall('userAuthTokenForExtension - start');

		const { data } = await userAuthTokenForExtensionMutationFunction({
			variables: {
				input: {
					extensionId,
					contextIds,
				},
			},
			context: {
				headers: {
					'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
				},
			},
		});

		if (data?.userAuthTokenForExtension) {
			const { success, authToken, errors } = data.userAuthTokenForExtension;

			tracing?.recordGqlCall('userAuthTokenForExtension - end');

			if (
				!success &&
				errors &&
				errors[0].message.includes('No Atlassian OAuth token found for this user and extension')
			) {
				return new Promise((resolve) => {
					renderThreeLOPrompt(async () => {
						const token = await fetchOAuthToken(extensionId, contextIds);
						resolve(token);
					});
				});
			}

			if (success && authToken) {
				const { token, ttl } = authToken;

				return { token, expiry: Date.now() + ttl * MILLIS_IN_SECOND };
			}
		}

		throw new Error('An unexpected error occurred when fetching an auth token');
	};

	const oauthFetch: (restPath: string, init: RequestInit) => Promise<Response> = async (
		restPath,
		init,
	) => {
		const requestAuthStartMark = getPerformanceMark();
		if (fg('forge-ui-performance-analytics-provider')) {
			captureEvent?.({
				type: 'product-api-request-initiated',
				elapsed: requestAuthStartMark,
			});
		}

		const atlassianAccount = !accountType || accountType === 'atlassian';
		if (!isTokenValid(authTokenRef.current) && atlassianAccount) {
			authTokenRef.current = await fetchOAuthToken(extensionId, contextIds);
		}

		const headers = new Headers(init.headers);
		headers.set('Authorization', `Bearer ${authTokenRef?.current?.token}`);

		if (fg('forge-ui-add-x-ecosystem-user-agent-header')) {
			// this header is used to identify requests made by Forge, based on:
			// https://hello.atlassian.net/wiki/x/O5jUIwE?atlOrigin=eyJpIjoiM2NhNjU1MDUxOWJkNDcxNThiNzJjNWU1NzE5Y2FmZTMiLCJwIjoiYyJ9

			const appAri = createAppAri({ appId }).toString();
			headers.set('x-ecosystem-user-agent', `forge ${appAri}`);
		}

		const requestAuthEndMark = getPerformanceMark();

		const request = fetch(new URL(`${getBaseUrl()}${restPath}`), {
			...init,
			body: init.body,
			headers,
		});

		if (fg('forge-ui-performance-analytics-provider')) {
			request.then(() => {
				const requestEndMark = getPerformanceMark();
				captureEvent?.({
					type: 'product-api-requested',
					duration: requestEndMark - requestAuthStartMark,
					authDuration: requestAuthEndMark - requestAuthStartMark,
					elapsed: requestEndMark,
				});
			});
		}

		return request;
	};
	const sessionFetch: (restPath: string, init: RequestInit) => Promise<Response> = async (
		restPath,
		init,
	) => {
		const request = fetch(`${getBaseUrl()}${restPath}`, {
			...init,
			body: init.body,
		});
		return request;
	};
	return {
		egressGet: async (payload: EgressGetPayload): Promise<Response> => {
			const { route, method } = EGRESS_GET();
			const init = {
				method,
			};

			const sp = new URLSearchParams();

			if (payload.keys && payload.keys.length > 0) {
				payload.keys.forEach((k) => {
					sp.append('keys', k);
				});
			}
			if (payload.pageSize) {
				sp.append('pageSize', `${payload.pageSize}`);
			}
			if (payload.nextPageToken) {
				sp.append('nextPageToken', payload.nextPageToken);
			}

			let routeToUse = route;
			if (sp.size > 0) {
				routeToUse = `${route}?${sp.toString()}`;
			}

			return oauthFetch(routeToUse, init);
		},
		egressSet: async (payload: EgressSetPayload, isDryRun: boolean): Promise<Response> => {
			const { route, method } = EGRESS_SET(installationId, isDryRun);
			const body = JSON.stringify(payload);
			const init = {
				method,
				body,
			};

			return sessionFetch(route, init);
		},
		egressDeleteDomain: async (payload: EgressDeleteDomainPayload): Promise<Response> => {
			const { route, method } = EGRESS_DELETE_DOMAIN(installationId);
			const body = JSON.stringify(payload);
			const init = {
				method,
				body,
			};

			return sessionFetch(route, init);
		},
		egressDeleteGroup: async (payload: EgressDeleteGroupPayload): Promise<Response> => {
			const { route, method } = EGRESS_DELETE_GROUP(installationId);
			const body = JSON.stringify(payload);
			const init = {
				method,
				body,
			};

			return sessionFetch(route, init);
		},
		remoteGet: async (payload: RemoteGetPayload): Promise<Response> => {
			const { route, method } = REMOTE_GET();
			const init = {
				method,
			};

			const sp = new URLSearchParams();

			if (payload.keys && payload.keys.length > 0) {
				payload.keys.forEach((k) => {
					sp.append('keys', k);
				});
			}
			if (payload.pageSize) {
				sp.append('pageSize', `${payload.pageSize}`);
			}
			if (payload.nextPageToken) {
				sp.append('nextPageToken', payload.nextPageToken);
			}

			let routeToUse = route;
			if (sp.size > 0) {
				routeToUse = `${route}?${sp.toString()}`;
			}

			return oauthFetch(routeToUse, init);
		},
		remoteSet: async (payload: RemoteSetPayload, isDryRun: boolean): Promise<Response> => {
			const { route, method } = REMOTE_SET(installationId, isDryRun);
			const body = JSON.stringify(payload);
			const init = {
				method,
				body,
			};

			return sessionFetch(route, init);
		},
	};
}
